import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpload } from '../contexts/UploadContext';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import PageAlert from './PageAlert';
import { trackPostScheduled, trackPostPublished } from '../services/analytics';

import { PLATFORM_CONFIG, PLATFORM_LIST } from './post/PLATFORM_CONFIG';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';
import PlatformCustomizationPanel from './post/PlatformCustomizationPanel';
import PostingProgressModal from './post/PostingProgressModal';
import SuggestionPanel from './post/SuggestionPanel';
import SmartComposeBar from './post/SmartComposeBar';
import { analyzeApi, youtubeApi, socialAccountsApi } from '../services/api';
import { io } from 'socket.io-client';
import { auth } from '../firebase';
import { LARGE_VIDEO_THRESHOLD_BYTES } from '../contexts/UploadContext';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';

import dayjs from 'dayjs';


// ─── Validation (no shared content — each platform is self-contained) ────────

function validateForm(selectedPlatforms, platformCustomizations) {
  const errors = {};

  if (selectedPlatforms.length === 0) {
    errors.platforms = 'Select at least one platform';
    return errors;
  }

  selectedPlatforms.forEach((platform) => {
    const c = platformCustomizations[platform] || {};
    const config = PLATFORM_CONFIG[platform];
    const hasMedia = !!c.media_file;
    const isScheduled = !!c.isScheduled;

    // Strict Media Requirement
    if (config?.isMediaRequired && !hasMedia) {
      errors[`${platform}_media`] = `Media (image/video) is required for ${config.label}`;
    }

    switch (platform) {
      case 'youtube':
        if (!c.title?.trim()) errors.youtube_title = 'Title is required for YouTube';
        break;
      case 'twitter':
        if (!c.text?.trim() && !hasMedia) errors.twitter_text = 'Tweet text or media is required';
        break;
      case 'reddit':
        if (!c.subreddit?.trim()) errors.reddit_subreddit = 'Subreddit is required';
        if (!c.title?.trim()) errors.reddit_title = 'Title is required for Reddit';
        if (isScheduled) errors.reddit_scheduled = 'Reddit scheduling is coming soon';
        break;
      case 'instagram':
        if (!c.text?.trim() && !hasMedia) errors.instagram_text = 'Caption or media is required';
        break;
      case 'facebook':
        if (!c.text?.trim() && !hasMedia) errors.facebook_text = 'Post text or media is required';
        if (isScheduled) errors.facebook_scheduled = 'Facebook scheduling is coming soon';
        break;
      case 'linkedin':
        if (!c.text?.trim() && !hasMedia) errors.linkedin_text = 'Post text or media is required';
        if (isScheduled) errors.linkedin_scheduled = 'LinkedIn does not support scheduling yet. Please toggle "Post Now".';
        break;
      default:
        break;
    }
  });

  return errors;
}

// ─── Platform header chip ─────────────────────────────────────────────────────

function PlatformChip({ platform, selected, onClick }) {
  const config = PLATFORM_CONFIG[platform.value];
  return (
    <Box
      onClick={onClick}
      data-testid={`platform-chip-${platform.value}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.5,
        py: 1,
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 800,
        fontSize: '0.85rem',
        color: selected ? 'white' : 'text.primary',
        userSelect: 'none',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        background: selected
          ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
          : alpha('#64748b', 0.05),
        border: '1px solid',
        borderColor: selected ? 'transparent' : alpha('#64748b', 0.1),
        boxShadow: selected ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none',
        '&:hover': {
          background: selected
            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            : alpha('#64748b', 0.1),
          transform: 'translateY(-1px)',
        },
      }}
    >
      {React.cloneElement(platform.icon, { sx: { fontSize: 18 } })}
      <span>{config.label}</span>
      {selected && <CheckCircleIcon sx={{ fontSize: 16, ml: 0.5 }} />}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PostContent = React.memo(({ onPost, error, initialScheduledDate: initialScheduledDateProp, initialPlatforms: initialPlatformsProp }) => {
  // Unified error extraction
  const errorMsg = typeof error === 'string' ? error : (error?.message || '');
  const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const location = useLocation();
  // Props take priority over router state (enables drawer usage without navigation)
  const initialScheduledDate = initialScheduledDateProp ?? location.state?.scheduledDate;
  const initialPlatforms = initialPlatformsProp ?? location.state?.initialPlatforms;

  // Filter available platforms by feature toggle allowed_platforms
  const { getAllowedPlatforms } = useFeatureToggles();
  const allowedPlatforms = getAllowedPlatforms(FEATURES.POSTING);
  const filteredPlatformList = PLATFORM_LIST.filter(p => allowedPlatforms.includes(p.value));

  // Default to first allowed platform; filter out any pre-selected platforms that are now disallowed
  const defaultPlatform = allowedPlatforms[0] || 'youtube';
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    initialPlatforms ? initialPlatforms.filter(p => allowedPlatforms.includes(p)) : [defaultPlatform]
  );
  const [expandedPlatform, setExpandedPlatform] = useState(
    initialPlatforms?.[0] && allowedPlatforms.includes(initialPlatforms[0]) ? initialPlatforms[0] : defaultPlatform
  );
  const [platformCustomizations, setPlatformCustomizations] = useState({});
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Posting modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [postingPlatforms, setPostingPlatforms] = useState([]);
  const [postingStatus, setPostingStatus] = useState({});
  const [postingResults, setPostingResults] = useState({});
  const [retryingPlatform, setRetryingPlatform] = useState(null);

  const { uploadFile, youtubeDirectUpload } = useUpload();
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState({}); // { platform: [account_id, ...] }

  // AI Suggestions state
  const [suggestionPanelOpen, setSuggestionPanelOpen] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionContext, setSuggestionContext] = useState(null); // { platform, field }
  const [aiError, setAiError] = useState(null);

  // Load connected social accounts for per-account selection
  useEffect(() => {
    socialAccountsApi.list()
      .then(data => setSocialAccounts(data.accounts || []))
      .catch(() => {}); // non-fatal — falls back to platform-level mode
  }, []);

  // Listen for background YouTube upload completion/failure
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001');
    socket.on('connect', async () => {
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          socket.emit('authenticate', { token });
        } catch {}
      }
    });
    socket.on('new_post', (data) => {
      if (data.platform === 'youtube') {
        setPostingStatus(prev => ({ ...prev, youtube: 'success' }));
        setPostingResults(prev => ({
          ...prev,
          youtube: { platform: 'youtube', status: 'success', post_id: data.post_id, error: null },
        }));
      }
    });
    socket.on('youtube_upload_failed', (data) => {
      setPostingStatus(prev => ({ ...prev, youtube: 'error' }));
      setPostingResults(prev => ({
        ...prev,
        youtube: { platform: 'youtube', status: 'error', error: data.error || 'YouTube upload failed' },
      }));
    });
    return () => socket.disconnect();
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePlatformToggle = useCallback((platformValue) => {
    setSelectedPlatforms((prev) => {
      const isSelected = prev.includes(platformValue);
      if (isSelected) {
        // Release File objects so the browser can GC large Blobs
        setPlatformCustomizations((c) => {
          if (!c[platformValue]) return c;
          const { media_file, thumbnail_file, ...rest } = c[platformValue]; // eslint-disable-line no-unused-vars
          return { ...c, [platformValue]: rest };
        });
        setExpandedPlatform((cur) => (cur === platformValue ? null : cur));
        return prev.filter((p) => p !== platformValue);
      } else {
        setExpandedPlatform(platformValue);
        return [...prev, platformValue];
      }
    });
  }, []);

  // Toggle one specific account (multi-account mode)
  const handleAccountToggle = useCallback((platform, accountId) => {
    setSelectedAccountIds((prev) => {
      const current = prev[platform] || [];
      const isSelected = current.includes(accountId);
      const updated = isSelected ? current.filter(id => id !== accountId) : [...current, accountId];
      const next = { ...prev, [platform]: updated };
      // Keep selectedPlatforms in sync
      setSelectedPlatforms(Object.keys(next).filter(p => next[p].length > 0));
      if (!isSelected) setExpandedPlatform(platform);
      return next;
    });
  }, []);

  const handleCustomizationChange = useCallback((platform, field, value) => {
    setPlatformCustomizations((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
    if (hasSubmitted) {
      setErrors((prev) => ({ ...prev, [`${platform}_${field}`]: undefined }));
    }
  }, [hasSubmitted]);

  // Handle pre-filled scheduled date from Calendar
  useEffect(() => {
    if (initialScheduledDate) {
      const date = dayjs(initialScheduledDate);
      if (date.isValid()) {
        const update = {};
        selectedPlatforms.forEach(p => {
          update[p] = {
            ...(platformCustomizations[p] || {}),
            isScheduled: true,
            scheduledDate: date
          };
        });
        setPlatformCustomizations(prev => ({ ...prev, ...update }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScheduledDate]); // Only run when initialScheduledDate changes (on mount usually)

  const handleAskAI = useCallback(async (platform) => {
    const customization = platformCustomizations[platform] || {};

    // Pre-flight validation: Check if there's any content to optimize
    const hasContent =
      customization.text?.trim() ||
      customization.title?.trim() ||
      customization.description?.trim() ||
      (Array.isArray(customization.tags) && customization.tags.length > 0) ||
      (Array.isArray(customization.hashtags) && customization.hashtags.length > 0);

    if (!hasContent) {
      setAiError(`Please provide some content (text, title, or tags) for ${platform} first so the AI has something to optimize!`);
      // Auto-clear error after 5 seconds
      setTimeout(() => setAiError(null), 5000);
      return;
    }

    setAiError(null);
    setSuggestionContext({ platform });
    setSuggestionPanelOpen(true);
    setSuggestionLoading(true);
    setSuggestions([]);

    try {
      const data = await analyzeApi.optimizeContent({
        platform,
        content_type: customization.content_type || 'post',
        text: customization.text || '',
        title: customization.title || '',
        tags: customization.tagsRaw || '',
        description: customization.description || '',
      });
      if (data.suggestions) {
        setSuggestions(data.suggestions);
      } else if (data.raw_result) {
        // Fallback for non-JSON suggestions if AI fails to format
        setSuggestions([{ text: data.raw_result, hook: 'General AI Advice' }]);
      }
    } catch (err) {
      console.error('AI Suggestion Error:', err);
      const msg = err?.message || (err?.isQuotaError
        ? 'AI quota reached. Please upgrade your plan.'
        : 'AI optimization failed. Please try again.');
      setAiError(msg);
      // Quota errors are important — keep visible until manually dismissed
      if (!err?.isQuotaError) {
        setTimeout(() => setAiError(null), 6000);
      }
      setSuggestionPanelOpen(false);
    } finally {
      setSuggestionLoading(false);
    }
  }, [platformCustomizations]);

  const handleApplySuggestion = useCallback((suggestion) => {
    if (!suggestionContext) return;
    const { platform } = suggestionContext;

    if (suggestion.title) handleCustomizationChange(platform, 'title', suggestion.title);
    if (suggestion.text) {
      if (platform === 'youtube') {
        handleCustomizationChange(platform, 'description', suggestion.text);
      } else {
        handleCustomizationChange(platform, 'text', suggestion.text);
      }
    }
    if (suggestion.hashtags) {
      if (platform === 'youtube') {
        const tagsArr = Array.isArray(suggestion.hashtags) ? suggestion.hashtags : suggestion.hashtags.split(',').map(t => t.trim());
        handleCustomizationChange(platform, 'tags', tagsArr);
      } else {
        const hashArr = Array.isArray(suggestion.hashtags) ? suggestion.hashtags : suggestion.hashtags.split(',').map(t => t.trim());
        const formattedHashes = hashArr.map(h => (h.startsWith('#') ? h : `#${h}`));
        handleCustomizationChange(platform, 'hashtags', formattedHashes);
      }
    }

    setSuggestionPanelOpen(false);
  }, [suggestionContext, handleCustomizationChange]);

  const handleSmartComposeResults = useCallback((results) => {
    setPlatformCustomizations((prev) => {
      const updated = { ...prev };
      Object.entries(results).forEach(([platform, fields]) => {
        if (!fields) return;
        const existing = updated[platform] || {};
        const patch = {};

        // Map AI fields to the customization shape each platform expects
        if (platform === 'youtube') {
          if (fields.title) patch.title = fields.title;
          if (fields.text) patch.description = fields.text;
          if (Array.isArray(fields.hashtags) && fields.hashtags.length > 0) {
            patch.tags = fields.hashtags;
            patch.tagsRaw = fields.hashtags.join(', ');
          }
        } else if (platform === 'reddit') {
          if (fields.title) patch.title = fields.title;
          if (fields.text) patch.text = fields.text;
          // Reddit has no hashtags
        } else {
          // Instagram, Twitter, Facebook, LinkedIn, TikTok
          if (fields.text) patch.text = fields.text;
          if (fields.title) patch.title = fields.title;
          if (Array.isArray(fields.hashtags) && fields.hashtags.length > 0) {
            patch.hashtags = fields.hashtags.map((h) =>
              h.startsWith('#') ? h : `#${h}`
            );
          }
        }

        updated[platform] = { ...existing, ...patch };
      });
      return updated;
    });
  }, []);

  const buildPosts = useCallback(
    (platformList = selectedPlatforms) => {
      const posts = [];
      platformList.forEach((platform) => {
        const c = platformCustomizations[platform] || {};

        // Append hashtags to the text payload for non-YouTube platforms
        let finalContentText = c.text || '';
        if (platform !== 'youtube' && Array.isArray(c.hashtags) && c.hashtags.length > 0) {
          finalContentText = finalContentText ? `${finalContentText}\n\n${c.hashtags.join(' ')}` : c.hashtags.join(' ');
        }

        const content = {
          ...c,
          text: finalContentText,
          media_file: undefined,
          thumbnail_file: undefined,
          ...(c.isScheduled && c.scheduledDate ? { scheduled_for: c.scheduledDate.toISOString() } : {})
        };

        const accountIds = selectedAccountIds[platform] || [];
        if (accountIds.length > 0) {
          // Multi-account: one post entry per selected account
          accountIds.forEach(account_id => posts.push({ platform, account_id, content }));
        } else {
          posts.push({ platform, content });
        }
      });
      return posts;
    },
    [selectedPlatforms, platformCustomizations, selectedAccountIds]
  );

  const buildMediaFiles = useCallback(
    (platformList = selectedPlatforms) => {
      const files = {};
      platformList.forEach((platform) => {
        const c = platformCustomizations[platform] || {};
        if (c.media_file) files[platform] = c.media_file;
        // YouTube thumbnail sent as a separate key
        if (platform === 'youtube' && c.thumbnail_file) {
          files['thumbnail_youtube'] = c.thumbnail_file;
        }
      });
      return files;
    },
    [selectedPlatforms, platformCustomizations]
  );

  const runPosting = useCallback(
    async (platformList, posts, mediaFiles) => {
      try {
        const results = await onPost(posts, mediaFiles);

        // If the parent handler swallowed the error (e.g. quota → upgrade modal)
        // and returned an empty array, close the posting modal so the user can
        // see the upgrade modal / error alert underneath.
        if (!results || results.length === 0) {
          setModalOpen(false);
          return [];
        }

        const newStatus = {};
        const newResults = {};
        for (const result of results || []) {
          // 'queued' means a background task (e.g. YouTube) is running — keep as
          // 'queued' so the UI shows a spinner rather than a false error.
          if (result.status === 'success') {
            newStatus[result.platform] = 'success';
          } else if (result.status === 'queued') {
            newStatus[result.platform] = 'queued';
          } else {
            newStatus[result.platform] = 'error';
          }
          newResults[result.platform] = result;
        }
        setPostingStatus((prev) => ({ ...prev, ...newStatus }));
        setPostingResults((prev) => ({ ...prev, ...newResults }));

        // Track successful platforms
        const successPlatforms = (results || [])
          .filter(r => r.status === 'success')
          .map(r => r.platform);
        if (successPlatforms.length > 0) {
          const isScheduled = platformList.some(
            p => platformCustomizations[p]?.isScheduled
          );
          if (isScheduled) {
            trackPostScheduled(successPlatforms, true);
          } else {
            trackPostPublished(successPlatforms);
          }
        }

        return results;
      } catch {
        const errorStatus = {};
        platformList.forEach((p) => { errorStatus[p] = 'error'; });
        setPostingStatus((prev) => ({ ...prev, ...errorStatus }));
        return [];
      }
    },
    [onPost, platformCustomizations]
  );

  const handleSubmit = useCallback(async () => {
    setHasSubmitted(true);
    const newErrors = validateForm(selectedPlatforms, platformCustomizations);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Auto-expand the first platform that has an error
      const platformWithError = selectedPlatforms.find((p) =>
        Object.keys(newErrors).some((key) => key.startsWith(`${p}_`))
      );
      if (platformWithError) setExpandedPlatform(platformWithError);
      return;
    }

    const platforms = [...selectedPlatforms];
    const posts = buildPosts(platforms);
    const mediaFiles = buildMediaFiles(platforms);

    // ── Detect large YouTube video BEFORE opening the modal ──────────────────
    // A video is "large" when it is ≥ LARGE_VIDEO_THRESHOLD_BYTES (500 MB).
    // Large YouTube videos bypass the backend streaming path: the browser uploads
    // the file directly to a YouTube resumable URI so Render never has to hold
    // gigabytes of video in memory.
    const ytCustom = platformCustomizations['youtube'] || {};
    const ytFile = ytCustom.media_file || null;
    const isLargeYouTubeVideo =
      platforms.includes('youtube') &&
      ytFile &&
      ytFile.size >= LARGE_VIDEO_THRESHOLD_BYTES;

    const initialStatus = Object.fromEntries(platforms.map((p) => [p, 'queued']));
    setPostingPlatforms(platforms);
    setPostingStatus(initialStatus);
    setPostingResults({});
    setModalOpen(true);

    platforms.forEach((platform, i) => {
      setTimeout(() => {
        setPostingStatus((prev) => ({ ...prev, [platform]: 'uploading' }));
      }, i * 200);
    });

    // ─── Phase 1: Cloud Staging — deduplicated R2 uploads ───────────────────
    //
    // Two optimisations applied here:
    //
    // 1. LARGE YOUTUBE SKIP — videos ≥ 500 MB going to YouTube skip R2 entirely.
    //    They go browser → YouTube directly in Phase 2a, so staging them to R2
    //    first would double the user's upload bandwidth for no benefit.
    //
    // 2. FILE DEDUPLICATION — if the user picks the same file for multiple
    //    platforms (e.g. the same 200 MB clip for Instagram + Facebook + Twitter),
    //    we upload to R2 exactly once and share the resulting mediaId across all
    //    platforms that sent the same file. Platforms are grouped by a fingerprint
    //    of (name + size + lastModified) — unique enough in practice.
    //
    // Example: 40 GB video → YouTube + Instagram
    //   YouTube  → skip R2, browser → YouTube directly         (Phase 2a)
    //   Instagram → R2 once (Instagram API needs a public URL)  (Phase 1)
    //   Net: user uploads 40 GB to YouTube + 40 GB to R2,
    //        but NOT 40 GB to R2 twice.
    //
    // Example: 200 MB clip → Instagram + Facebook + Twitter
    //   Before: 3 × 200 MB = 600 MB total R2 uploads
    //   After:  1 × 200 MB = 200 MB total R2 upload, mediaId shared

    // Fingerprint a File object — stable within a single browser session
    const _fileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

    // Build groups: fileKey → { file, platforms[] }
    // Exclude large YouTube (handled in Phase 2a) and platforms with no media
    const fileGroups = {};
    platforms.forEach((platform) => {
      if (platform === 'youtube' && isLargeYouTubeVideo) return;
      const file = platformCustomizations[platform]?.media_file;
      if (!file) return;
      const key = _fileKey(file);
      if (!fileGroups[key]) fileGroups[key] = { file, platforms: [] };
      fileGroups[key].platforms.push(platform);
    });

    const stagingPromises = [];
    const mediaIds = {};

    Object.values(fileGroups).forEach(({ file, platforms: sharedPlatforms }) => {
      // Mark all platforms in this group as staging
      sharedPlatforms.forEach((p) =>
        setPostingStatus(prev => ({ ...prev, [p]: 'staging_cloud' }))
      );

      // Upload once — callback fires with a single mediaId
      const stagingPromise = new Promise((resolve, reject) => {
        uploadFile(file, (url, mediaId) => {
          if (mediaId) {
            // Assign the same mediaId to every platform that shares this file
            sharedPlatforms.forEach((p) => { mediaIds[p] = mediaId; });
            resolve();
          } else {
            reject(new Error(`Cloud staging failed for ${sharedPlatforms.join(', ')}`));
          }
        });
      });
      stagingPromises.push(stagingPromise);
    });

    let stagingFullyFailed = false;
    try {
      await Promise.all(stagingPromises);
    } catch (e) {
      console.error('One or more cloud staging tasks failed:', e);
      let anyNonYoutubeStaged = false;
      platforms.forEach((p) => {
        // Large YouTube skips R2 — don't mark it as failed if R2 staging fails
        if (p === 'youtube' && isLargeYouTubeVideo) return;
        if (platformCustomizations[p]?.media_file && !mediaIds[p]) {
          setPostingStatus((prev) => ({ ...prev, [p]: 'error' }));
          setPostingResults((prev) => ({
            ...prev,
            [p]: { platform: p, status: 'error', error: 'Media upload failed. Please try again.' },
          }));
        } else if (mediaIds[p]) {
          anyNonYoutubeStaged = true;
        }
      });
      // Only abort entirely if there is nothing left to post AND no large YouTube direct upload
      if (!anyNonYoutubeStaged && !isLargeYouTubeVideo) return;
      stagingFullyFailed = !anyNonYoutubeStaged;
    }

    // ─── Phase 2a: Large YouTube — direct browser → YouTube upload ────────────
    // Runs only when the video is ≥ 500 MB AND YouTube is selected.
    // The regular backend streaming path is skipped for YouTube in this case.
    let youtubeHandledDirectly = false;
    if (isLargeYouTubeVideo) {
      setPostingStatus(prev => ({ ...prev, youtube: 'uploading' }));
      try {
        const ytContent = posts.find(p => p.platform === 'youtube')?.content || {};
        const videoId = await youtubeDirectUpload(
          ytFile,
          {
            title:         ytContent.title        || '',
            description:   ytContent.description  || ytContent.text || '',
            tags:          ytContent.tags          || [],
            categoryId:    ytContent.category_id   || '22',
            privacyStatus: ytContent.privacy_status || 'public',
            scheduledFor:  ytContent.scheduled_for  || null,
            contentType:   ytContent.content_type   || 'video',
          },
          (pct) => {
            // Reflect XHR upload progress in the modal (staging_cloud maps to the
            // progress bar the PostingProgressModal already knows how to render)
            setPostingStatus(prev => ({ ...prev, youtube: pct < 100 ? 'staging_cloud' : 'uploading' }));
          },
        );

        // Finalise on the backend: set thumbnail + save to Firestore.
        // No mediaId passed — large video was never staged to R2, so there's
        // nothing for the cleanup service to delete.
        await youtubeApi.completeDirectUpload({
          youtubeVideoId: videoId,
          content: ytContent,
        });

        setPostingStatus(prev => ({ ...prev, youtube: 'success' }));
        setPostingResults(prev => ({
          ...prev,
          youtube: { platform: 'youtube', status: 'success', post_id: videoId, error: null },
        }));
        youtubeHandledDirectly = true;
      } catch (err) {
        console.error('[YouTube Direct Upload] Failed:', err);
        setPostingStatus(prev => ({ ...prev, youtube: 'error' }));
        setPostingResults(prev => ({
          ...prev,
          youtube: {
            platform: 'youtube',
            status:   'error',
            error:    err.message || 'Direct YouTube upload failed. Please try again.',
          },
        }));
        youtubeHandledDirectly = true; // still handled — don't retry via backend
      }
    }

    // ─── Phase 2b: Final Posting — all platforms except direct-uploaded YouTube ─
    // Also skip platforms that failed staging (mediaIds not populated for them)
    const platformsForBackend = platforms.filter(p => {
      if (p === 'youtube' && youtubeHandledDirectly) return false;
      if (platformCustomizations[p]?.media_file && !mediaIds[p] &&
          !(p === 'youtube' && isLargeYouTubeVideo)) return false; // staging failed
      return true;
    });

    if (!stagingFullyFailed && platformsForBackend.length > 0) {
      const finalPosts = posts
        .filter(p => platformsForBackend.includes(p.platform))
        .map(p => {
          if (mediaIds[p.platform]) {
            return { ...p, content: { ...p.content, mediaId: mediaIds[p.platform] } };
          }
          return p;
        });

      // Strip raw file bytes for any platform whose media is already in R2.
      // Sending the File object again alongside mediaId causes a 413 at the proxy.
      const stagedMediaFiles = Object.fromEntries(
        Object.entries(mediaFiles).filter(([key]) => !mediaIds[key])
      );

      await runPosting(platformsForBackend, finalPosts, stagedMediaFiles);
    }
  }, [selectedPlatforms, platformCustomizations, buildPosts, buildMediaFiles, runPosting, uploadFile, youtubeDirectUpload]);

  const handleRetry = useCallback(
    async (platform, content) => {
      setRetryingPlatform(platform);
      setPostingStatus((prev) => ({ ...prev, [platform]: 'uploading' }));
      const posts = [{ platform, content }];
      const mediaFiles = buildMediaFiles([platform]);
      await runPosting([platform], posts, mediaFiles);
      setRetryingPlatform(null);
    },
    [buildMediaFiles, runPosting]
  );

  // 'queued' is terminal — background task is running, user can close and will be notified via socket.
  const allDone =
    postingPlatforms.length > 0 &&
    postingPlatforms.every((p) => ['success', 'error', 'queued'].includes(postingStatus[p]));

  const platformsWithErrors = selectedPlatforms.filter((p) =>
    Object.keys(errors).some((key) => key.startsWith(`${p}_`))
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  // Priority Early Return: If accounts aren't connected, show the "Locked" onboarding state
  if (isNoAccountsError) {
    return (
      <Box sx={{ py: 6 }}>
        <ConnectPlatformsCTA
          title="Posting Hub Locked"
          description="Connect your social media platforms in Settings to broadcast your story across your entire network from one dashboard."
          buttonText="Link Your Platforms"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
            Multi-Platform Posting
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Broadcast your story across your entire social network from one place
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {filteredPlatformList.map((platformDef) => {
            const platformAccounts = socialAccounts.filter(a => a.platform === platformDef.value);
            if (platformAccounts.length > 1) {
              // Multi-account: show one chip per connected account
              return platformAccounts.map((acct) => {
                const isSelected = (selectedAccountIds[platformDef.value] || []).includes(acct.account_id);
                const label = acct.display_name || acct.ig_username || acct.page_name || acct.channel_name || platformDef.label;
                return (
                  <PlatformChip
                    key={acct.account_id}
                    platform={{ ...platformDef, label }}
                    selected={isSelected}
                    onClick={() => handleAccountToggle(platformDef.value, acct.account_id)}
                  />
                );
              });
            }
            // Single account or no accounts: platform-level chip (legacy behavior)
            return (
              <PlatformChip
                key={platformDef.value}
                platform={platformDef}
                selected={selectedPlatforms.includes(platformDef.value)}
                onClick={() => handlePlatformToggle(platformDef.value)}
              />
            );
          })}
        </Box>
      </Box>

      <PageAlert error={error} />

      {errors.platforms && (
        <PageAlert error={errors.platforms} severity="error" sx={{ mb: 4 }} />
      )}

      {aiError && (
        <PageAlert
          error={aiError}
          severity="warning"
          sx={{ mb: 4 }}
          onClose={() => setAiError(null)}
        />
      )}

      <Card
        elevation={0}
        sx={{
          background: isDark
            ? alpha(theme.palette.background.paper, 0.6)
            : 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.1 : 0.3)}`,
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 20px 50px -20px rgba(0,0,0,0.4)'
            : '0 20px 50px -20px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {selectedPlatforms.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 80, height: 80, mx: 'auto', mb: 3 }}>
                  <SendIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                  Ready to go viral?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Select one or more platforms above to start crafting your post.
                </Typography>
              </Box>
            ) : (
              <>
                <SmartComposeBar
                  selectedPlatforms={selectedPlatforms}
                  onApplyResults={handleSmartComposeResults}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {selectedPlatforms.map((platform) => (
                    <PlatformCustomizationPanel
                      key={platform}
                      platform={platform}
                      customization={platformCustomizations[platform] || {}}
                      onChange={handleCustomizationChange}
                      errors={errors}
                      showErrors={hasSubmitted}
                      expanded={expandedPlatform === platform}
                      onExpandToggle={() =>
                        setExpandedPlatform(expandedPlatform === platform ? null : platform)
                      }
                      hasError={hasSubmitted && platformsWithErrors.includes(platform)}
                      onAskAI={() => handleAskAI(platform)}
                    />
                  ))}
                </Box>

                <SuggestionPanel
                  open={suggestionPanelOpen}
                  onClose={() => setSuggestionPanelOpen(false)}
                  loading={suggestionLoading}
                  suggestions={suggestions}
                  onApply={handleApplySuggestion}
                  platform={suggestionContext?.platform}
                />

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    data-testid="submit-post-button"
                    startIcon={<SendIcon />}
                    size="large"
                    sx={{
                      borderRadius: '4px',
                      px: 8,
                      py: 2,
                      fontWeight: 900,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      boxShadow: '0 8px 25px -5px rgba(99, 102, 241, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        boxShadow: '0 12px 30px -5px rgba(99, 102, 241, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {selectedPlatforms.some(p => platformCustomizations[p]?.isScheduled) ? 'Schedule' : 'Launch'} Post to {selectedPlatforms.length} Channel{selectedPlatforms.length > 1 ? 's' : ''}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <PostingProgressModal
        open={modalOpen}
        platforms={postingPlatforms}
        postingStatus={postingStatus}
        results={postingResults}
        retryingPlatform={retryingPlatform}
        onRetry={handleRetry}
        onClose={() => setModalOpen(false)}
        allDone={allDone}
      />
    </Box >
  );
});

export default PostContent;
